import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "item"];
  static values = {
    existingItems: Array,
    existingItemAttributions: Object,
  }

  declare readonly inputTarget: HTMLInputElement;
  declare readonly itemTargets: HTMLElement[];
  declare readonly existingItemsValue: string[];
  // oxlint-disable-next-line @typescript-eslint/no-wrapper-object-types
  declare readonly existingItemAttributionsValue: Object;

  private selected: Set<string> = new Set();
  private existingItems: Set<string> = new Set();
  private existingAttributions: Map<string, string> = new Map();

  connect(): void {
    this.existingItems = new Set(this.existingItemsValue);

    this.existingAttributions = new Map(
      Object.entries(this.existingItemAttributionsValue)
        .map(([key, value]) => [key, typeof value == 'string' ? value : JSON.stringify(value)])
    );

    this.selected = new Set(this.existingItemsValue);
    this.updateInput();
    this.updateAllVisuals();
  }

  toggle(event: Event): void {
    const item = event.currentTarget as HTMLElement;
    const value = item.dataset.value;
    const selectable = item.dataset.selectable !== "false";
    const isMuted = item.classList.contains('range-selector-item--muted');

    if (!value || !selectable || isMuted) {
      return;
    }

    if (this.selected.has(value)) {
      this.selected.delete(value)
    } else {
      this.selected.add(value)
    }

    this.updateInput();
    this.updateAllVisuals();
  }

  clearAll(): void {
    this.selected.clear();
    this.updateInput();
    this.updateAllVisuals();
  }

  reset(): void {
    this.selected = new Set(this.existingItems);
    this.updateInput();
    this.updateAllVisuals();
  }

  private updateAllVisuals(): void {
    this.itemTargets.forEach(item => {
      const value = item.dataset.value;
      if (value) {
        // Clear all state classes
        item.classList.remove(
          "range-selector-item--existing-selected",
          "range-selector-item--new-selected",
          "range-selector-item--existing-deselected"
        );

        const isSelected = this.selected.has(value);
        const wasExisting = this.existingItems.has(value);
        const attribution = this.existingAttributions.get(value);
        item.removeAttribute('title');

        if (isSelected && wasExisting) {
          item.classList.add("range-selector-item--existing-selected");
          if (attribution) {
            item.title = 'Closed by ' + attribution;
          }
        } else if (isSelected && !wasExisting) {
          item.classList.add("range-selector-item--new-selected");
          item.title = "Newly closed";
        } else if (!isSelected && wasExisting) {
          item.classList.add("range-selector-item--existing-deselected");
          if (attribution) {
            item.title = 'Was closed by ' + attribution;
          }
        }
      }
    });
  }

  private updateInput(): void {
    this.inputTarget.value = Array.from(this.selected).sort().join(',');
  }
}